import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Outlet, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { FirebaseAuthProvider } from './context/FirebaseAuthContext';
import { useFirebaseAuth } from './context/FirebaseAuthContext';
import { AuthProvider } from './context/AuthContext';
import { SidebarProvider } from './context/SidebarContext';
import { UserPersonaProvider } from './context/UserPersonaContext';
import { ImpersonationProvider } from './context/ImpersonationContext';
import TopBar from './components/TopBar';
import Toolbar from './components/Toolbar';
import InterclubTermsDialog from './components/InterclubTermsDialog';
import OnboardingWizard from './components/OnboardingWizard';
import Home from './pages/Home';
import Dashboard from './pages/Dashboard';
import Qualifiers from './pages/Qualifiers';
import Competitions from './pages/Competitions';
import Registration from './pages/Registration';
import Profile from './pages/Profile';
import Skaters from './pages/Skaters';
import SkaterDetail from './pages/SkaterDetail';
import Auth from './pages/Auth';
import Admin from './pages/Admin';
import InterclubAdmin from './pages/InterclubAdmin';
import Rules from './pages/Rules';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import { REGISTRATION_BASE_URL } from './config/environment';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  components: {
    MuiBox: {
      styleOverrides: {
        root: {
          maxWidth: 'none',
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          maxWidth: 'none',
          padding: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          maxWidth: 'none',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          maxWidth: 'none',
          width: '100%',
        },
        html: {
          maxWidth: 'none',
          width: '100%',
        },
        '#root': {
          maxWidth: 'none',
          width: '100%',
        }
      },
    },
  },
});

// Main layout with sidebar and top bar
function MainLayout() {
  const { user, loading, adminStatus, adminStatusLoading, isInterclubAdmin, acceptAdminTerms, getIdToken } =
    useFirebaseAuth();
  const navigate = useNavigate();
  const [onboardingStatus, setOnboardingStatus] = useState({ loading: true, complete: true });

  const checkOnboardingStatus = useCallback(async () => {
    if (!user) {
      setOnboardingStatus({ loading: false, complete: true });
      return;
    }

    try {
      const token = await getIdToken();
      if (!token) {
        setOnboardingStatus({ loading: false, complete: true });
        return;
      }

      const response = await fetch(`${REGISTRATION_BASE_URL}/onboarding-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        setOnboardingStatus({
          loading: false,
          complete: data.onboardingComplete === true,
          roles: data.roles,
          approvalStatus: data.approvalStatus,
        });
      } else {
        // If we get a 404 or error, assume onboarding not complete
        setOnboardingStatus({ loading: false, complete: false });
      }
    } catch (error) {
      console.error('Failed to check onboarding status:', error);
      // On error, don't block the user - assume complete
      setOnboardingStatus({ loading: false, complete: true });
    }
  }, [user, getIdToken]);

  useEffect(() => {
    checkOnboardingStatus();
  }, [checkOnboardingStatus]);

  const handleOnboardingComplete = (result) => {
    setOnboardingStatus((prev) => ({ ...prev, complete: true }));

    // If user selected parent or skater role, redirect to registration
    const selectedRoles = result?.selectedRoles || [];
    if (selectedRoles.includes('parent') || selectedRoles.includes('skater')) {
      navigate('/registration');
    }
  };

  const requiresTerms =
    !!user &&
    !adminStatusLoading &&
    isInterclubAdmin() &&
    adminStatus.termsRequiredVersion &&
    adminStatus.termsVersion !== adminStatus.termsRequiredVersion;

  // Show loading while checking onboarding status
  if (loading || onboardingStatus.loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show onboarding wizard if user is logged in but hasn't completed onboarding
  if (user && !onboardingStatus.complete) {
    return <OnboardingWizard open={true} onComplete={handleOnboardingComplete} />;
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', overflow: 'hidden' }}>
      <InterclubTermsDialog
        open={requiresTerms}
        termsVersion={adminStatus.termsRequiredVersion}
        onAccept={acceptAdminTerms}
      />
      {/* Sidebar */}
      <Toolbar />
      
      {/* Main content area with top bar and content */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        flexGrow: 1,
        minWidth: 0, // Allow content to shrink below its natural size
        overflow: 'hidden' // Prevent overflow
      }}>
        {/* Top Bar - positioned beside sidebar */}
        <TopBar />
        
        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            width: '100%',
            minHeight: '100vh',
            pt: 2, // Reduced padding since top bar is beside sidebar
            pl: { xs: 2, md: 3, lg: 4 }, // Reduced side padding
            pr: { xs: 2, md: 3, lg: 4 }, // Reduced side padding
            pb: 0,
            transition: 'all 0.2s ease-in-out',
            overflow: 'auto', // Allow vertical scrolling within content
            // Ensure content fills the available width without overflowing
            '& > *': {
              width: '100%',
              maxWidth: '100%',
              minWidth: 0
            },
            // Override any Material-UI default constraints
            '& .MuiContainer-root': {
              maxWidth: '100% !important',
              padding: '0 !important'
            },
            '& .MuiBox-root': {
              maxWidth: '100% !important',
              minWidth: 0
            },
            // Additional overrides for any remaining constraints
            '& .MuiPaper-root': {
              maxWidth: '100% !important',
              minWidth: 0
            },
            '& .MuiGrid-root': {
              maxWidth: '100% !important',
              minWidth: 0
            }
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <FirebaseAuthProvider>
        <AuthProvider>
          <ImpersonationProvider>
            <UserPersonaProvider>
              <SidebarProvider>
              <Router>
                <Routes>
                  <Route path="/auth" element={<Auth />} />
                  <Route element={<MainLayout />}>
                    <Route path="/" element={<Home />} />
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/qualifiers" element={<Qualifiers />} />
                    <Route path="/competitions" element={<Competitions />} />
                    <Route path="/registration" element={<Registration />} />
                    <Route path="/registration/season-fee/success" element={<Registration />} />
                    <Route path="/registration/season-fee/cancel" element={<Registration />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/skaters" element={<Skaters />} />
                    <Route path="/skaters/:skaterId" element={<SkaterDetail />} />
                    <Route path="/rules" element={<Rules />} />
                    <Route path="/privacy" element={<PrivacyPolicy />} />
                    <Route path="/terms" element={<TermsOfService />} />
                    <Route path="/admin/*" element={<Admin />} />
                    <Route path="/interclub-admin/*" element={<InterclubAdmin />} />
                  </Route>
                </Routes>
              </Router>
              </SidebarProvider>
            </UserPersonaProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </FirebaseAuthProvider>
    </ThemeProvider>
  );
}

export default App;